import React from 'react'
import { useConfiguration } from '../contexts/ConfigurationContext'
import { Server, Cpu, MemoryStick, HardDrive } from 'lucide-react'

function InfrastructureConfig() {
  const { state, dispatch } = useConfiguration()

  const handleInstanceTypeChange = (instanceType) => {
    dispatch({ type: 'SET_INSTANCE_TYPE', payload: instanceType })
  }

  const handleVcpuChange = (vcpus) => {
    dispatch({ type: 'SET_VCPUS', payload: parseInt(vcpus) })
    // Reset instance type to "Custom" when manually changing values
    if (state.instanceType !== 'Custom') {
      dispatch({ type: 'SET_INSTANCE_TYPE', payload: 'Custom' })
    }
  }

  const handleRamChange = (ramGb) => {
    dispatch({ type: 'SET_RAM', payload: parseInt(ramGb) })
    // Reset instance type to "Custom" when manually changing values
    if (state.instanceType !== 'Custom') {
      dispatch({ type: 'SET_INSTANCE_TYPE', payload: 'Custom' })
    }
  }

  const handleStorageChange = (storageGb) => {
    dispatch({ type: 'SET_STORAGE', payload: parseInt(storageGb) })
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-4">
        <Server className="h-4 w-4 text-primary-600" />
        <h3 className="text-base font-semibold text-gray-900">
          Infrastructure Configuration
        </h3>
      </div>

      <div className="space-y-4">
        {/* Instance Type Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Instance Type
          </label>
          <select
            value={state.instanceType}
            onChange={(e) => handleInstanceTypeChange(e.target.value)}
            className="input-field"
          >
            {state.instanceTypes.map((type) => (
              <option key={type.name} value={type.name}>
                {type.name} ({type.vcpus} vCPU, {type.ramGb}GB RAM) - {type.description}
              </option>
            ))}
            <option value="Custom">Custom Configuration</option>
          </select>
        </div>

        {/* vCPUs Configuration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center space-x-2">
              <Cpu className="h-4 w-4" />
              <span>Virtual CPUs</span>
            </div>
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="1"
              max="32"
              value={state.vcpus}
              onChange={(e) => handleVcpuChange(e.target.value)}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max="32"
                value={state.vcpus}
                onChange={(e) => handleVcpuChange(e.target.value)}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
              />
              <span className="text-sm text-gray-500">vCPUs</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            1 vCPU = 1 virtual core with dedicated compute capacity
          </p>
        </div>

        {/* RAM Configuration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center space-x-2">
              <MemoryStick className="h-4 w-4" />
              <span>Memory (RAM)</span>
            </div>
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="1"
              max="128"
              value={state.ramGb}
              onChange={(e) => handleRamChange(e.target.value)}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max="128"
                value={state.ramGb}
                onChange={(e) => handleRamChange(e.target.value)}
                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
              />
              <span className="text-sm text-gray-500">GB</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Memory for applications and system processes
          </p>
        </div>

        {/* Storage Configuration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center space-x-2">
              <HardDrive className="h-4 w-4" />
              <span>Storage</span>
            </div>
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="10"
              max="1000"
              step="10"
              value={state.storageGb}
              onChange={(e) => handleStorageChange(e.target.value)}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="10"
                max="1000"
                step="10"
                value={state.storageGb}
                onChange={(e) => handleStorageChange(e.target.value)}
                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
              />
              <span className="text-sm text-gray-500">GB</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            SSD storage for OS, applications, and data
          </p>
        </div>

        {/* Configuration Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Configuration Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="text-lg font-semibold text-primary-600">{state.vcpus}</div>
              <div className="text-gray-500">vCPUs</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-primary-600">{state.ramGb}GB</div>
              <div className="text-gray-500">RAM</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-primary-600">{state.storageGb}GB</div>
              <div className="text-gray-500">Storage</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InfrastructureConfig 